import { Banner, Button } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { displayError } from "../api/errors.js";
import {
  createPlanDraft,
  listPlanInstances,
  publishPlan,
} from "../plan/plan-api.js";
import { listTaskDefinitions } from "../tasks/tasks-api.js";
import { checksOutOfDate } from "./plan-sync.js";
import { assetResource, cleaningResource } from "./resources.js";
import { listSetupItems } from "./setup-api.js";

// Offers to refresh the plan's checks after fridges or cleaning changed.
export function UpdateChecks({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient();
  const [waiting, setWaiting] = useState(false);
  const plans = useQuery({
    queryFn: () => listPlanInstances(locationId),
    queryKey: ["plan-instances", locationId],
  });
  const published = plans.data?.find((plan) => plan.state === "published");
  const draft = plans.data?.find((plan) => plan.state === "draft");
  const assets = useQuery({
    enabled: Boolean(published),
    queryFn: () => listSetupItems(assetResource, locationId),
    queryKey: ["setup", assetResource.key, locationId],
  });
  const cleaning = useQuery({
    enabled: Boolean(published),
    queryFn: () => listSetupItems(cleaningResource, locationId),
    queryKey: ["setup", cleaningResource.key, locationId],
  });
  const definitions = useQuery({
    enabled: Boolean(published),
    queryFn: () => listTaskDefinitions(locationId),
    queryKey: ["dashboard", "schedules", locationId],
    refetchInterval: waiting ? 3_000 : false,
  });
  const update = useMutation({
    mutationFn: async () => {
      const target =
        draft ??
        (await createPlanDraft({
          clone_current: true,
          location_id: locationId,
          plan_pack_code: published!.plan_pack_code,
        }));
      await publishPlan(target.id);
    },
    onSuccess: async () => {
      setWaiting(true);
      globalThis.setTimeout(() => setWaiting(false), 30_000);
      await Promise.all(
        [
          ["plan-instances", locationId],
          ["plan-checks", locationId],
          ["generated-plan-tasks", locationId],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });

  if (!published || !assets.data || !cleaning.data || !definitions.data)
    return null;
  const count = checksOutOfDate(
    assets.data,
    cleaning.data,
    definitions.data.items,
  );
  if (count === 0) return null;

  return (
    <Banner
      action={
        <Button
          disabled={update.isPending || waiting}
          onClick={() => update.mutate()}
          size="sm"
          variant="outline"
        >
          {update.isPending || waiting ? "Updating…" : "Update checks"}
        </Button>
      }
      title={
        update.error
          ? displayError(update.error)
          : count === 1
            ? "1 check is out of date"
            : `${count} checks are out of date`
      }
      tone="attention"
    />
  );
}
